import { useEffect } from "react";

// Generic confirm-before-action dialog: title, message, confirm/cancel labels, a confirm
// callback. Built for the "Request Payout" flow (the second financial-adjacent action needing
// an explicit confirm step, after the flashcards leave-dialog) but deliberately kept generic so
// any future confirm-before-action flow reuses this instead of growing another one-off.
//
// FlashcardsLeaveConfirmDialog (src/App.js) is NOT retrofitted onto this component — it's a
// genuine three-way choice (Save/Discard/Cancel), a different shape than the plain confirm/
// cancel binary this models, not an oversight.
//
// `danger` (optional, default false): renders the confirm button in the destructive-red style
// instead of the default maroon primary style. A payout request isn't destructive, so it's left
// false there; a future "delete X" style flow would pass true.
export function ConfirmDialog({ open, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, busy = false, danger = false }) {
  // Escape closes it like clicking the overlay does — but never while a confirm is already in
  // flight (busy), so a stray keypress can't abandon an action that's already been submitted.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div className="confirm-dialog-overlay" onClick={() => { if (!busy) onCancel(); }}>
      <div
        className="confirm-dialog-box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-dialog-title" id="confirm-dialog-title">{title}</div>
        <p className="confirm-dialog-body">{message}</p>
        <div className="confirm-dialog-actions">
          <button type="button" className="settings-cancel-btn" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`confirm-dialog-confirm-btn${danger ? " danger" : ""}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
